import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom'
import { FormProvider, useForm } from './contexts/FormContext'
import { useEffect } from 'react'
import Layout from './components/layout/Layout'
import MainLayout from './components/layout/MainLayout'
import { ProgressBar, Toast, ResumeProgressModal } from './components/common'
import Step1SiteBase from './pages/steps/Step1SiteBase'
import Step2DesignBase from './pages/steps/Step2DesignBase'
import Step3Customization from './pages/steps/Step3Customization'
import Step4Pages from './pages/steps/Step4Pages'
import Step5Addons from './pages/steps/Step5Addons'
import Step6Contact from './pages/steps/Step6Contact'
import Step7Review from './pages/steps/Step7Review'
import ThankYouPage from './pages/ThankYouPage'
import EditRequest from './pages/EditRequest'

// Component to sync URL with current step, provide navigate, and handle route protection
function StepSync() {
  const { currentStep, setCurrentStep, setNavigate, formData, isLoading } = useForm()
  const location = useLocation()
  const navigate = useNavigate()

  // Provide navigate function to context
  useEffect(() => {
    setNavigate(() => navigate)
  }, [navigate, setNavigate])

  // Route protection and step sync - ONLY after initial load complete
  useEffect(() => {
    // Don't do anything while loading saved data
    if (isLoading) {
      console.log('Still loading, skipping route protection')
      return
    }

    const match = location.pathname.match(/\/step\/(\d+)/)
    if (!match) return

    const requestedStep = parseInt(match[1])

    // Check if localStorage has data
    const hasLocalStorage = localStorage.getItem('dlr_form_v1')

    // If no saved data and trying to access steps > 1, redirect to step 1
    if (!hasLocalStorage && requestedStep > 1) {
      console.log('No saved data, redirecting to step 1')
      navigate('/step/1', { replace: true })
      return
    }

    // Validate step progression - can't skip ahead
    if (requestedStep > 1 && hasLocalStorage) {
      const canAccessStep = validateStepAccess(requestedStep, formData)
      if (!canAccessStep) {
        // Find the highest valid step
        let validStep = 1
        for (let i = 1; i < requestedStep; i++) {
          if (validateStepAccess(i + 1, formData)) {
            validStep = i + 1
          } else {
            break
          }
        }
        console.log(`Cannot access step ${requestedStep}, redirecting to step ${validStep}`)
        navigate(`/step/${validStep}`, { replace: true })
        return
      }
    }

    // Sync currentStep with URL if different
    if (requestedStep !== currentStep) {
      console.log(`Syncing currentStep from ${currentStep} to ${requestedStep}`)
      setCurrentStep(requestedStep)
    }
  }, [location.pathname, isLoading, formData, currentStep]) // Include necessary deps

  return null
}

// Validate if user can access a specific step
function validateStepAccess(step, formData) {
  switch (step) {
    case 1:
      return true // Always can access step 1
    case 2:
      return formData.siteBase !== '' // Need to select package
    case 3:
      return formData.designApproach !== '' // Need to select design
    case 4:
      return formData.logoOption !== '' // Need to select logo option
    case 5:
      return formData.pages.default.every(p => p.configured) // All pages must be configured
    case 6:
      return formData.addons.domain.option !== '' // Need to select domain
    case 7:
      return formData.contact.companyName && formData.contact.contactName && formData.contact.email // Contact info required
    default:
      return false
  }
}

// Wrapper to access FormContext for progress bar
function AppContent() {
  const { currentStep, toast, hideToast, showResumeModal, lastSaved, handleResumeProgress, handleStartFresh } = useForm()
  const location = useLocation()

  // Hide progress bar and sidebar on thank you and edit pages
  const hideFormUI = location.pathname.startsWith('/thank-you') || location.pathname.startsWith('/edit')

  return (
    <Layout>
      <StepSync />
      {!hideFormUI && <ProgressBar currentStep={currentStep} totalSteps={7} />}
      {hideFormUI ? (
        <Routes>
          <Route path="/thank-you/:token" element={<ThankYouPage />} />
          <Route path="/edit/:token" element={<EditRequest />} />
        </Routes>
      ) : (
      <MainLayout>
        <Routes>
          <Route path="/" element={<Navigate to="/step/1" replace />} />
          <Route path="/step/1" element={<Step1SiteBase />} />
          <Route path="/step/2" element={<Step2DesignBase />} />
          <Route path="/step/3" element={<Step3Customization />} />
          <Route path="/step/4" element={<Step4Pages />} />
          <Route path="/step/5" element={<Step5Addons />} />
          <Route path="/step/6" element={<Step6Contact />} />
          <Route path="/step/7" element={<Step7Review />} />
          <Route path="*" element={<Navigate to="/step/1" replace />} />
        </Routes>
      </MainLayout>
      )}

      {/* Toast Notifications */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={hideToast}
        />
      )}

      {/* Resume Progress Modal */}
      {showResumeModal && lastSaved && (
        <ResumeProgressModal
          lastSaved={lastSaved}
          onResume={handleResumeProgress}
          onStartFresh={handleStartFresh}
        />
      )}
    </Layout>
  )
}

function App() {
  return (
    <Router>
      <FormProvider>
        <AppContent />
      </FormProvider>
    </Router>
  )
}

export default App
